/**
 * settingsBusca.js — achar um parâmetro pelo nome, sem percorrer o menu
 * CI Sistemas de Informática Ltda. — AcessoiD (Onda 104)
 *
 * A tela Configurações tem doze seções e mais de oitenta parâmetros. O menu lateral
 * resolve "onde ficam as coisas de impressão"; não resolve "onde fica a Janela de
 * Abandono" para quem não lembra se ela é cobrança, reconciliação ou passagem. A busca
 * responde isso: digita-se um pedaço do nome, e a tela leva ao campo.
 *
 * O QUE ELA PROCURA, em ordem de peso: o rótulo do campo, a chave técnica
 * (`max_parking_duration` — é o que aparece em log e em conversa de suporte), o título
 * do grupo e da seção, e por último o texto da explicação. Sem acento e sem caixa:
 * "cameras" acha "Câmeras". Várias palavras são um E: "placa minima" acha os dois
 * campos que têm as duas.
 *
 * O QUE ELA RESPEITA: a vertical. No Clube Social a busca não oferece Cobrança nem
 * Impressão — seria levar a uma seção que não existe ali.
 *
 * O QUE ELA NÃO ESCONDE: o parâmetro que existe mas não está à vista AGORA (os do modo
 * Similaridade, com a instalação no modo Passagem). Ele aparece no resultado com o
 * aviso de por que não está na tela — sumir com ele faria o operador concluir que o
 * parâmetro não existe.
 *
 * Função pura: recebe o termo e o contexto, devolve a lista. A página só desenha.
 */
import { SETTINGS_META, reiniciaOMotor } from './settingsMeta';
import { ESTRUTURA, blocoDasChaves, valeNaVertical } from './settingsEstrutura';

/** "Câmeras" → "cameras". */
export const semAcento = (texto) => String(texto || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * O radical da palavra buscada: "mínima" tem de achar "Mínimo", "câmeras" tem de achar
 * "Câmera". Só em palavra de cinco letras ou mais, e só o `s` e a vogal do fim — o
 * bastante para gênero e plural, sem virar um motor de busca.
 */
export const radical = (palavra) => (palavra.length >= 5 ? palavra.replace(/s$/, '').replace(/[aeo]$/, '') : palavra);

/**
 * Parâmetros que só existem dentro de um bloco com desenho próprio e não têm entrada em
 * SETTINGS_META. Sem isto eles seriam INALCANÇÁVEIS pela busca — o teste "nenhum
 * parâmetro fica inalcançável" pegou o Modo de Filtragem exatamente assim.
 */
const META_DE_BLOCO = {
  plate_filter_mode: {
    label: 'Modo de Filtragem',
    description: 'A estratégia que junta as leituras de um mesmo veículo: Consolidação por passagem (recomendado), Nenhum, Similaridade ou Dupla Leitura. Filtro anti-leitura duplicada.',
  },
};

const MAXIMO = 12;

/** Todo parâmetro da tela, com o caminho até ele. Montado uma vez. */
function montarIndice() {
  const itens = [];
  for (const grupoDoMenu of ESTRUTURA) {
    for (const secao of grupoDoMenu.secoes) {
      for (const grupo of secao.grupos) {
        for (const item of grupo.itens) {
          const chaves = typeof item === 'string' ? [item] : (blocoDasChaves[item.bloco] || []);
          for (const chave of chaves) {
            const meta = SETTINGS_META[chave] || META_DE_BLOCO[chave];
            if (!meta) continue;
            itens.push({
              chave, meta, secao, grupo, grupoDoMenu: grupoDoMenu.grupo,
              // Campo dentro de um bloco com desenho próprio (os sliders de limiar, os
              // cartões do modo) não tem linha própria na tela: a busca leva à seção.
              temLinha: typeof item === 'string',
              alvo: {
                rotulo: semAcento(meta.label),
                chave: semAcento(chave).replace(/_/g, ' '),
                chaveCrua: chave.toLowerCase(),
                onde: semAcento(`${grupo.titulo || ''} ${secao.titulo} ${grupoDoMenu.grupo}`),
                descricao: semAcento(meta.description),
              },
            });
          }
        }
      }
    }
  }
  return itens;
}

let indice = null;

/**
 * @param {string} termo
 * @param {{vertical?: string|null, settings?: object, cobranca?: boolean, superadmin?: boolean}} contexto
 * @returns {Array<{chave, rotulo, secaoId, secaoTitulo, caminho, temLinha, recolhido,
 *                  reiniciaOMotor, foraDeVista: string|null, trecho: string|null}>}
 */
export function buscarParametro(termo, { vertical = null, settings = {}, cobranca = false, superadmin = false } = {}) {
  const extra = { cobranca, superadmin };
  const digitadas = semAcento(termo).replace(/_/g, ' ').split(/\s+/).filter(Boolean);
  // Uma letra só casaria com quase tudo: a lista seria o menu inteiro, fora de ordem.
  if (digitadas.length === 0 || digitadas.join('').length < 2) return [];
  const palavras = digitadas.map(radical);
  if (!indice) indice = montarIndice();

  const achados = [];
  for (const it of indice) {
    if (!valeNaVertical(it.secao, vertical, extra) || !valeNaVertical(it.grupo, vertical, extra)) continue;

    let pontos = 0;
    let todas = true;
    let naDescricao = null;
    for (const p of palavras) {
      if (it.alvo.rotulo.includes(p)) pontos += it.alvo.rotulo.startsWith(p) ? 12 : 10;
      else if (it.alvo.chave.includes(p) || it.alvo.chaveCrua.includes(p)) pontos += 7;
      else if (it.alvo.onde.includes(p)) pontos += 4;
      else if (it.alvo.descricao.includes(p)) { pontos += 1; naDescricao = naDescricao || p; }
      else { todas = false; break; }
    }
    if (!todas) continue;

    const visivel = !it.grupo.visivelSe || it.grupo.visivelSe(settings);
    achados.push({
      chave: it.chave,
      rotulo: it.meta.label,
      secaoId: it.secao.id,
      secaoTitulo: it.secao.titulo,
      caminho: [it.grupoDoMenu, it.secao.titulo, it.grupo.titulo].filter(Boolean).join(' › '),
      temLinha: it.temLinha,
      recolhido: !!it.grupo.recolhido,
      reiniciaOMotor: reiniciaOMotor(it.chave) && !it.meta.disabled,
      foraDeVista: visivel ? null : 'Não aparece agora: pertence a outro modo de filtragem.',
      trecho: naDescricao ? trechoCom(it.meta.description, naDescricao) : null,
      pontos,
    });
  }

  // Mais pontos primeiro; no empate, a ordem do menu (estável: o índice já vem nela).
  return achados
    .map((a, i) => ({ a, i }))
    .sort((x, y) => (y.a.pontos - x.a.pontos) || (x.i - y.i))
    .slice(0, MAXIMO)
    .map(({ a }) => { const { pontos, ...resto } = a; return resto; });
}

/** Um pedaço da explicação em volta da palavra achada, para o resultado dizer POR QUE apareceu. */
function trechoCom(descricao, palavra) {
  const texto = String(descricao || '');
  const pos = semAcento(texto).indexOf(palavra);
  if (pos < 0) return null;
  const de = Math.max(0, pos - 40);
  const ate = Math.min(texto.length, pos + palavra.length + 60);
  return `${de > 0 ? '…' : ''}${texto.slice(de, ate).trim()}${ate < texto.length ? '…' : ''}`;
}
